import { useState, useRef } from "react";
import { Send, Paperclip, Loader2, Lock } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { AttachmentPreview } from "./AttachmentPreview";
import { useUploadAttachment } from "../../hooks/useAttachments";

const ACCEPTED_TYPES =
  "image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,.md,.csv";
const ALLOWED_MIME = new Set([
  "image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf", "text/plain", "text/markdown", "text/csv",
]);
const MAX_ATTACHMENT_MB = 15; // el mismo tope que el servidor

// Nombre legible para una captura pegada (el portapapeles las llama «image.png»).
function nameForPasted(file) {
  if (file.name && file.name !== "image.png") return file;
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
  return new File([file], `captura-${stamp}.${ext}`, { type: file.type });
}

export function MessageInput({ onSend, disabled, onOpenCredentials }) {
  const [value, setValue] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachError, setAttachError] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const uploadAttachment = useUploadAttachment();

  const hasContent = value.trim().length > 0 || pendingAttachments.length > 0;
  const isUploading = uploadAttachment.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasContent || disabled || isUploading) return;
    onSend(
      value.trim(),
      pendingAttachments.map((a) => a.id)
    );
    setValue("");
    setPendingAttachments([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  // Sube archivos por el mismo camino venga de donde venga (clip, Ctrl+V o arrastrar y soltar), con avisos claros.
  const uploadFiles = async (files) => {
    setAttachError("");
    for (const file of files) {
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setAttachError(`«${file.name}» pesa más de ${MAX_ATTACHMENT_MB} MB; no se adjuntó.`);
        continue;
      }
      if (file.type && !ALLOWED_MIME.has(file.type) && !/\.(md|csv)$/i.test(file.name || "")) {
        setAttachError(`«${file.name}» no es de un tipo permitido (imágenes, PDF o texto); no se adjuntó.`);
        continue;
      }
      try {
        const uploaded = await uploadAttachment.mutateAsync(file);
        setPendingAttachments((prev) => [...prev, { ...uploaded, _file: file }]);
      } catch (err) {
        setAttachError(err?.response?.data?.detail || `No se pudo adjuntar «${file.name}». Inténtalo de nuevo.`);
      }
    }
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(files);
  };

  // Ctrl+V con una imagen en el portapapeles (captura de pantalla): se adjunta sola. El texto pegado no se toca.
  const handlePaste = (e) => {
    if (disabled || isUploading) return;
    const images = Array.from(e.clipboardData?.items || [])
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (images.length === 0) return;
    e.preventDefault();
    uploadFiles(images.map(nameForPasted));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || isUploading) return;
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };

  const handleRemoveAttachment = (id) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <form
      onSubmit={handleSubmit}
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      data-testid="message-form"
      className={`sticky bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 py-4 ${dragging ? "ring-2 ring-emerald-500/60" : ""}`}
    >
      <div className="max-w-3xl mx-auto space-y-2">
        {attachError && (
          <p data-testid="attach-error" className="text-xs text-red-500">
            {attachError}
          </p>
        )}
        {pendingAttachments.length > 0 && (
          <p data-testid="attach-privacy-note" className="text-[11px] text-muted-foreground">
            Las imágenes viajan a la IA como cualquier mensaje: no adjuntes pantallas con contraseñas ni datos de clientes visibles.
          </p>
        )}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pendingAttachments.map((att) => (
              <AttachmentPreview
                key={att.id}
                attachment={att}
                localFile={att._file}
                onRemove={() => handleRemoveAttachment(att.id)}
              />
            ))}
          </div>
        )}
        <div className="flex items-end gap-2 rounded-xl border border-border bg-background p-2 shadow-sm">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={handleFilesSelected}
          />
          {onOpenCredentials && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="credentials-button"
              title="Compartir credenciales (IP, usuario y contraseña)"
              onClick={onOpenCredentials}
            >
              <Lock className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            data-testid="attach-file-button"
            disabled={disabled || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          </Button>
          <Textarea
            data-testid="chat-input-textarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Escribe un mensaje… (puedes pegar una captura con Ctrl+V)"
            disabled={disabled}
            className="min-h-[52px] max-h-40 resize-none border-0 shadow-none focus-visible:ring-0"
          />
          <Button
            data-testid="chat-send-button"
            type="submit"
            disabled={disabled || !hasContent || isUploading}
            size="icon"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p data-testid="credentials-hint" className="text-[11px] text-muted-foreground">
          🔒 Para compartir IP, usuario y contraseña usa el candado (o pégalos aquí): el panel los guarda cifrados y la IA no los ve.
        </p>
      </div>
    </form>
  );
}
